import { supabase } from './supabase'
import { DEFAULT_DUE_DATE_PAYLOAD, normalizeDueDatePayload, type DueDatePayload } from './dueDateGroups'

export type DueDateConfigRow = {
  id: number
  config_key: 'vencimentos_grupo'
  effective_from: string
  title: string
  payload: DueDatePayload
  release_id: number | null
}

export type DueDateConfigSource = 'online' | 'cache' | 'fallback'

const CACHE_KEY = 'suite.v8.operational-config.vencimentos_grupo'

const FALLBACK_ROW: DueDateConfigRow = {
  id: -1,
  config_key: 'vencimentos_grupo',
  effective_from: '2026-08-28',
  title: 'Regra inicial de vencimentos por grupo',
  payload: DEFAULT_DUE_DATE_PAYLOAD,
  release_id: null,
}

function readCache(): DueDateConfigRow[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return []
    const rows = JSON.parse(raw) as Array<Omit<DueDateConfigRow, 'payload'> & { payload: unknown }>
    return rows.map((row) => ({ ...row, payload: normalizeDueDatePayload(row.payload) }))
  } catch {
    return []
  }
}

function writeCache(rows: DueDateConfigRow[]) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(rows))
  } catch {
    // Cache é contingência e não bloqueia a consulta operacional.
  }
}

export async function loadDueDateConfigs(): Promise<{ rows: DueDateConfigRow[]; source: DueDateConfigSource }> {
  const result = await supabase
    .from('operational_configs')
    .select('id,config_key,effective_from,title,payload,release_id')
    .eq('config_key', 'vencimentos_grupo')
    .order('effective_from', { ascending: true })

  if (!result.error && result.data && result.data.length > 0) {
    const rows = result.data.map((row) => ({
      ...(row as Omit<DueDateConfigRow, 'payload'> & { payload: unknown }),
      payload: normalizeDueDatePayload(row.payload),
    }))
    writeCache(rows)
    return { rows, source: 'online' }
  }

  const cached = readCache()
  if (cached.length > 0) return { rows: cached, source: 'cache' }
  return { rows: [FALLBACK_ROW], source: 'fallback' }
}

export function selectDueDateConfig(rows: DueDateConfigRow[], referenceDate: string) {
  return [...rows]
    .filter((row) => row.effective_from <= referenceDate)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? rows.at(-1) ?? FALLBACK_ROW
}

export async function loadCurrentDueDateConfig() {
  const loaded = await loadDueDateConfigs()
  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return { config: selectDueDateConfig(loaded.rows, today), source: loaded.source }
}
